import { useMemo } from 'react';

/**
 * Hook: Dynamic scheduling terminology based on UI mode
 *
 * Returns appropriate labels and UI elements based on the school's scheduling approach.
 * Accepts mode as a parameter for testability and reusability.
 *
 * Terminology mapping:
 * - Traditional mode: Uses "Period/Periods" for both classes and blocks
 * - Elementary mode: Uses "Class/Classes" for classes and "Block/Blocks" for blocks
 * - Both mode: Uses "Class/Classes" for classes and "Block/Blocks" for blocks
 *
 * @param mode - Scheduling UI mode ('traditional', 'elementary', or 'both')
 * @returns Object with terminology labels, links, and visibility flags
 *
 * @example
 * const { tenant } = useAuthStore();
 * const mode = tenant?.settings?.schedulingUiMode || 'both';
 * const { classLabel, blockLabel, myBlocksLabel } = useSchedulingTerminology(mode);
 */
export function useSchedulingTerminology(mode: 'traditional' | 'elementary' | 'both') {
  return useMemo(
    () => ({
      mode,

      // === CLASS TERMINOLOGY ===
      // Labels for single entity
      classLabel: mode === 'traditional' ? 'Period' : 'Class',
      // Labels for plural entities
      classesLabel: mode === 'traditional' ? 'Periods' : 'Classes',
      // Action labels
      createClassLabel: mode === 'traditional' ? 'Create Period' : 'Create Class',
      manageClassesLabel: mode === 'traditional' ? 'Manage Periods' : 'Manage Classes',

      // === BLOCK TERMINOLOGY ===
      // In traditional mode, we call "blocks" as "periods" since each period has one block
      // In elementary/both mode, blocks are distinct from classes
      blockLabel: mode === 'traditional' ? 'Period' : 'Block',
      blocksLabel: mode === 'traditional' ? 'Periods' : 'Blocks',

      // Teacher dashboard
      myBlocksLabel: mode === 'traditional' ? 'My Periods' : 'My Blocks',
      noBlocksMessage: mode === 'traditional' ? 'No periods scheduled for today.' : 'No blocks scheduled for today.',
      goToBlockLabel: mode === 'traditional' ? 'Go to Period' : 'Go to Block',

      // Admin dashboard
      totalBlocksLabel: mode === 'traditional' ? 'Total Periods' : 'Total Blocks',

      // Filters and dropdowns
      allBlocksAndLocationsLabel: mode === 'traditional' ? 'All Periods & Locations' : 'All Blocks & Locations',
      classroomBlocksLabel: mode === 'traditional' ? 'CLASSROOM PERIODS' : 'CLASSROOM BLOCKS',
      byBlockLabel: mode === 'traditional' ? 'By Period' : 'By Block',

      // Block overrides (only used in elementary/both, but provide fallback)
      blockOverridesLabel: mode === 'traditional' ? 'Period Overrides' : 'Block Overrides',
      manageBlockOverridesLabel: mode === 'traditional' ? 'Manage Period Overrides' : 'Manage Block Overrides',

      // Staff assignments
      staffBlockLabel: mode === 'traditional' ? 'Period' : 'Block',
      entireClassAllBlocksLabel: mode === 'traditional' ? 'Entire Class (all periods)' : 'Entire Class (all blocks)',

      // Page links
      classesPageLink: mode === 'traditional' ? '/admin/periods' : '/admin/classes',
      blocksPageLink: '/admin/blocks',

      // UI visibility flags
      showBlocksUI: mode !== 'traditional',
      showPeriodsUI: mode !== 'elementary',
    }),
    [mode]
  );
}
